import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  getStoredSession,
  isSupabaseAuthConfigured,
  saveStoredSession,
  signInWithPassword,
  signOut,
  type SupabaseSession,
} from "@/lib/supabaseAuth";

type AuthContextValue = {
  session: SupabaseSession | null;
  isAuthenticated: boolean;
  isConfigured: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SupabaseSession | null>(null);

  useEffect(() => {
    setSession(getStoredSession());
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: Boolean(session?.access_token),
      isConfigured: isSupabaseAuthConfigured(),
      signIn: async (email: string, password: string) => {
        const next = await signInWithPassword(email, password);
        setSession(next);
      },
      signOut: () => {
        signOut();
        saveStoredSession(null);
        setSession(null);
      },
    }),
    [session],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

